import * as React from 'react';

import { ChevronDown, ChevronUp } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';
import { Select as SelectPrimitive } from 'radix-ui';

function Select({
  ...props
}: Readonly<React.ComponentProps<typeof SelectPrimitive.Root>>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className="data-placeholder:text-text-placeholder"
      {...props}
    />
  );
}

function SelectTrigger({
  className,
  children,
  disabled = false,
  readOnly = false,
  size = 'small',
  color = 'default',
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: 'small' | 'medium';
  color?: 'default' | 'warning' | 'error';
  readOnly?: boolean;
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      data-color={color}
      disabled={disabled}
      aria-disabled={disabled}
      aria-readonly={readOnly}
      className={cn(
        'group/select-trigger inline-flex min-w-fit items-center justify-between gap-2 rounded-sm border border-border-primary bg-ui-field02 fill-icon-primary px-3 text-[14px] text-text-primary hover:border-action-focusBorder focus:outline-action-focusBorder disabled:border-border-disabled disabled:bg-action-disabledBackground disabled:text-text-placeholder disabled:focus:outline-none data-placeholder:text-text-placeholder data-[size=medium]:h-10 data-[size=small]:h-8 [&[data-state=open]>svg]:rotate-180',
        //warning color variant
        'data-[color=warning]:border-2 data-[color=warning]:border-warning-main data-[color=warning]:focus-visible:border-warning-main data-[color=warning]:focus-visible:outline-none data-[color=warning]:disabled:border',
        //error color variant
        'data-[color=error]:border-2 data-[color=error]:border-error-main data-[color=error]:focus-visible:border-error-main data-[color=error]:focus-visible:outline-none data-[color=error]:disabled:border',
        //read only variant
        'aria-readonly:bg-ui-field01 aria-readonly:fill-icon-secondary aria-readonly:text-text-secondary aria-readonly:hover:border-border-primary aria-readonly:focus-visible:border aria-readonly:focus-visible:border-border-primary aria-readonly:focus-visible:outline-none',
        //disabled variant
        'aria-disabled:border-border-disabled aria-disabled:bg-action-disabledBackground aria-disabled:fill-text-disabled02 aria-disabled:text-text-placeholder aria-disabled:focus:outline-none',
        className,
      )}
      {...props}
    >
      {children}

      <SelectPrimitive.Icon className="fill-icon-secondary transition-transform duration-300 group-data-[state=open]/select-trigger:rotate-180">
        <ChevronDown />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = 'popper',
  container,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content> & {
  /**
   * Specify a container element to portal the content into.
   * @default document.body
   */
  container?: HTMLDivElement | null;
}) {
  return (
    <SelectPrimitive.Portal container={container}>
      <SelectPrimitive.Content
        data-slot="select-content"
        sideOffset={4}
        className={cn(
          'z-popover flex w-(--radix-popper-anchor-width) rounded-sm bg-ui-primary text-[14px] text-text-primary shadow-[0px_4px_16px_rgba(0,0,0,0.25)]',
          className,
        )}
        position={position}
        {...props}
      >
        <SelectPrimitive.Viewport className={cn('rounded-sm')}>
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn(
        'bg-ui-primary px-3 pt-4 pb-1 text-[14px] leading-3 font-normal text-text-secondary uppercase',
        className,
      )}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  startAdornment,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item> & {
  startAdornment?: React.ReactNode;
}) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        'flex h-10 cursor-pointer items-center bg-ui-primary fill-icon-primary px-3 py-1.5 text-[14px] leading-4 font-normal text-text-primary focus:bg-ui-hover focus:outline-none data-disabled:text-text-disabled data-disabled:hover:bg-ui-primary data-[state=checked]:bg-background-tertiary data-[state=checked]:fill-tertiary-main data-[state=checked]:text-tertiary-dark data-[state=checked]:focus:bg-ui-highlight data-[state=checked]:focus:outline-none',
        className,
      )}
      {...props}
    >
      {startAdornment && <span className="mr-2">{startAdornment}</span>}
      <SelectPrimitive.ItemText data-slot="select-item-text">
        {children}
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn(className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        'flex cursor-default items-center justify-center fill-icon-secondary py-1',
        className,
      )}
      {...props}
    >
      <ChevronUp />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        'flex cursor-default items-center justify-center fill-icon-secondary py-1',
        className,
      )}
      {...props}
    >
      <ChevronDown />
    </SelectPrimitive.ScrollDownButton>
  );
}

function SelectTriggerUnstyled({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger-unstyled"
      className={className}
      {...props}
    >
      {children}
    </SelectPrimitive.Trigger>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectTriggerUnstyled,
  SelectValue,
};
